import { Controller } from "@hotwired/stimulus"
import { createUndoStack } from "../../../lib/work/undo"
import { ensureAnnouncer, announce } from "../../../lib/work/announce"

// Work kit backlog surface engine (W3 Track A) — the FIRST surface engine
// over the W1 drag kernel. The backlog controller is stacked with the kernel
// (and the W3 selection engine) on one root:
//
//   data-controller="ui--work-backlog ui--work-selection ui--work-drag"
//
// It owns ZERO drag mechanics: the kernel performs every gesture and applies
// the optimistic DOM move. This engine only TRANSLATES the kernel's
// ui--work:drop into the backlog's three domain events, all bubbling
// CustomEvents on the root (events are the only API — anti-idea 2):
//
//   ui--work:rank-reorder   { container, moved_ids[], before_id, after_id,
//                             gesture_token, source }
//                             RESTORES (undo/redo) emit the same commit
//                             shape with an additive action: "undo"|"redo"
//                             field and NO gesture_token/source (tokenless
//                             by the sansToken contract); a non-contiguous
//                             moved set emits one commit per contiguous run
//                             in the restored order
//   ui--work:backlog-drop   { issue_ids[], from_container, to_container,
//                             kind: "rank"|"membership", before_id|null,
//                             after_id|null, gesture_token, source }
//   ui--work:assign         { issue_ids[], kind: "epic"|"version",
//                             target_id, gesture_token, source }
//
// Payload discipline (anti-idea 3): neighbour deltas only — before_id /
// after_id against the container's order with the moved ids REMOVED, never
// index math, never full arrays. A rank drop whose detail carries an index/
// position field (or non-string moved ids) is rejected at the gate below:
// the kernel move is reverted and nothing is emitted.
//
// Container-id grammar (the host's contract with this engine):
//   backlog             — the unscheduled list (rank container)
//   sprint:<id>         — a sprint section list (rank container)
//   marker:sprint:<id>  — the hover drop-zone row above a sprint section;
//                         drops here mean MEMBERSHIP (add to sprint), never
//                         rank — the optimistic move is reverted immediately
//   epic:<id>           — rail node; drop = assign epic (revert + emit)
//   version:<id>        — rail node; drop = assign fix version (revert + emit)
//
// The marker/rail prefixes are kernel-internal container ids ONLY: the
// emitted backlog-drop payload always names the sprint as
// to_container "sprint:<id>" with null neighbours, so hosts never see the
// marker namespace.

const DROP_EVENT = "ui--work:drop"
const DRAG_START_EVENT = "ui--work:drag-start"
const DRAG_CANCEL_EVENT = "ui--work:drag-cancel"
const RANK_REORDER_EVENT = "ui--work:rank-reorder"
const BACKLOG_DROP_EVENT = "ui--work:backlog-drop"
const ASSIGN_EVENT = "ui--work:assign"
const ISSUE_OPEN_EVENT = "ui--work:issue-open"
const SEARCH_REQUEST_EVENT = "ui--work:search-request"
const SELECTION_REQUEST_EVENT = "ui--work:selection-request"
const SPRINT_ACTION_EVENT = "ui--work:sprint-action"
const SPRINT_COMPLETE_EVENT = "ui--work:sprint-complete"

const MARKER_PREFIX = "marker:"
const RAIL_PREFIXES = { "epic:": "epic", "version:": "version" }
const UNDO_CAP = 100
const SKELETON_ROWS = 3
// Turbo dispatches NOTHING on a hard frame-load network failure (its
// requestErrored only console.errors upstream), so every Turbo-path lazy
// load arms this one-shot unwedge; turbo:frame-missing (response arrived,
// no matching frame) is the other failure signal.
const FRAME_LOAD_TIMEOUT = 15000

// Announcer copy — {token} interpolation via lib/work/announce. Hosts
// override any subset through the announcements String value (JSON Object);
// missing keys fall back to these defaults.
const DEFAULT_ANNOUNCEMENTS = {
  moved: "Moved {issue_key}",
  moved_many: "Moved {count} work items",
  selected: "{count} work items selected",
  selection_cleared: "Selection cleared",
  loaded: "{count} more work items loaded",
  undone: "Change undone",
  redone: "Change redone",
}

export default class BacklogController extends Controller {
  // Host-overridable announcer copy (JSON Object as a String value, the
  // board/timeline hydration pattern). Emitted only when non-empty.
  static values = {
    announcements: String,
  }

  connect() {
    if (this._bound) return // Turbo restoration: idempotent connect
    this._bound = true

    this._announcements = { ...DEFAULT_ANNOUNCEMENTS }
    if (this.hasAnnouncementsValue && this.announcementsValue !== "") {
      try {
        Object.assign(this._announcements, JSON.parse(this.announcementsValue))
      } catch {
        // A malformed overrides value falls back to the defaults — loud in
        // the host's console, never a broken surface.
        console.error("[ui--work-backlog] announcements value is not valid JSON:", this.announcementsValue)
      }
    }

    this._undo = createUndoStack(UNDO_CAP)
    this._redoEntries = []
    this._cursorEl = null
    this._collapsed = new Map() // sprint id -> collapsed boolean (post-toggle)
    this._dragSelection = null // { container, ids[] } origin-order snapshot
    this._gestureOrigin = null // { container, order[], all[] } pre-gesture
    this._gestureActive = false
    this._inFlight = new Set() // section ids with a lazy page loading
    this._frameTimeouts = new Map() // section id -> lazy-load unwedge timeout
    this._observedSentinels = new Map() // sentinel element -> IntersectionObserver
    this._announcer = ensureAnnouncer(this.element)

    this._onKernelDrop = (event) => this._translateKernelDrop(event)
    this._onKernelDragStart = (event) => this._captureGestureStart(event)
    this._onKernelCancel = () => this._clearGestureState()
    this._boundRootClick = (event) => this._onRootClick(event)
    this._onKeydown = (event) => this._handleKeydown(event)
    this._boundFocusIn = (event) => this._onFocusIn(event)
    this._boundFrameLoad = (event) => this._onFrameLoad(event)
    this._boundFrameMissing = (event) => this._onFrameMissing(event)
    this._boundRailSelect = (event) => this._onRailSelect(event)

    this.element.addEventListener(DROP_EVENT, this._onKernelDrop)
    this.element.addEventListener(DRAG_START_EVENT, this._onKernelDragStart)
    this.element.addEventListener(DRAG_CANCEL_EVENT, this._onKernelCancel)
    this.element.addEventListener("click", this._boundRootClick)
    this.element.addEventListener("keydown", this._onKeydown)
    this.element.addEventListener("focusin", this._boundFocusIn)
    this.element.addEventListener("turbo:frame-load", this._boundFrameLoad)
    this.element.addEventListener("turbo:frame-missing", this._boundFrameMissing)
    this.element.addEventListener("tree:select", this._boundRailSelect)

    this._armSentinels()
    this._setInitialCursor()

    // Host re-renders (Turbo morphs, section re-paints) reset SSR state —
    // re-apply collapsed sections, cursor tabindex and sentinel wiring.
    this._mutation = new MutationObserver(() => this._scheduleReapply())
    this._mutation.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    if (!this._bound) return
    this._bound = false

    this.element.removeEventListener(DROP_EVENT, this._onKernelDrop)
    this.element.removeEventListener(DRAG_START_EVENT, this._onKernelDragStart)
    this.element.removeEventListener(DRAG_CANCEL_EVENT, this._onKernelCancel)
    this.element.removeEventListener("click", this._boundRootClick)
    this.element.removeEventListener("keydown", this._onKeydown)
    this.element.removeEventListener("focusin", this._boundFocusIn)
    this.element.removeEventListener("turbo:frame-load", this._boundFrameLoad)
    this.element.removeEventListener("turbo:frame-missing", this._boundFrameMissing)
    this.element.removeEventListener("tree:select", this._boundRailSelect)

    for (const timer of this._frameTimeouts.values()) clearTimeout(timer)
    this._frameTimeouts.clear()
    for (const observer of this._observedSentinels.values()) observer.disconnect()
    this._observedSentinels.clear()
    if (this._mutation) {
      this._mutation.disconnect()
      this._mutation = null
    }
    this._clearGestureState()
  }

  // ── Keyboard map (Track A item 7) ────────────────────────────────────────
  // j/k cursor, o/Enter open, `.` search, Space toggle-select (bridged to the
  // selection engine through ui--work:selection-request — the same seam the
  // W11 navigator uses), Escape clears selection + refocuses the list.
  // Alt+Arrow rank is the KERNEL's gesture: this handler bails on Alt so the
  // two never double-handle. Form fields never trigger surface keys.
  _handleKeydown(event) {
    if (event.defaultPrevented) return
    const target = event.target
    if (!(target instanceof Element)) return
    if (this._isFormField(target)) return

    const key = event.key
    if ((event.metaKey || event.ctrlKey) && !event.altKey && (key === "z" || key === "Z")) {
      event.preventDefault()
      if (event.shiftKey) this.redo()
      else this.undo()
      return
    }
    if (event.metaKey || event.ctrlKey || event.altKey) return
    // Sprint completion modal open — the surface keyboard stands down.
    const modal = target.closest("[data-work-sprint-modal]")
    if (modal && !modal.hasAttribute("hidden")) return

    const row = target.closest("[data-work-row-id]") ?? this._cursorEl
    switch (key) {
      case "j":
      case "J":
        event.preventDefault()
        this._moveCursor(1)
        return
      case "k":
      case "K":
        event.preventDefault()
        this._moveCursor(-1)
        return
      case "o":
      case "O":
      case "Enter": {
        if (!row) return
        event.preventDefault()
        this._setCursor(row)
        this._emit(ISSUE_OPEN_EVENT, {
          issue_id: row.getAttribute("data-work-row-id"),
          source: "keyboard",
        })
        return
      }
      case ".": {
        if (!row) return
        event.preventDefault()
        this._setCursor(row)
        this._emit(SEARCH_REQUEST_EVENT, {
          issue_id: row.getAttribute("data-work-row-id"),
          source: "keyboard",
        })
        return
      }
      case " ":
      case "Spacebar": {
        if (!row) return
        event.preventDefault()
        this._setCursor(row)
        this._emit(SELECTION_REQUEST_EVENT, {
          issue_id: row.getAttribute("data-work-row-id"),
          mode: "toggle",
        })
        return
      }
      case "Escape": {
        if (this._gestureActive) return // the kernel owns Esc during a gesture
        // An open sprint-completion modal owns Esc: close it, emit nothing.
        const openModal = this.element.querySelector("[data-work-sprint-modal]:not([hidden])")
        if (openModal) {
          openModal.setAttribute("hidden", "")
          return
        }
        this._emit(SELECTION_REQUEST_EVENT, { mode: "clear" })
        this._announceKey("selection_cleared")
        ;(this._cursorEl ?? this._rows()[0])?.focus()
        return
      }
    }
  }

  // ── Roving cursor ────────────────────────────────────────────────────────
  // Rows carry tabindex="-1" from the server; the cursor row gets 0. The
  // kernel never fights this: it only sets tabindex=0 on items WITHOUT one.
  _rows() {
    return [...this.element.querySelectorAll("[data-work-row-id]")]
  }

  _rowById(id) {
    if (typeof id !== "string" || id === "") return null
    return this.element.querySelector(`[data-work-row-id="${CSS.escape(id)}"]`)
  }

  _moveCursor(delta) {
    const rows = this._rows()
    if (!rows.length) return
    const index = this._cursorEl ? rows.indexOf(this._cursorEl) : -1
    const next = Math.min(rows.length - 1, Math.max(0, (index === -1 ? 0 : index) + delta))
    this._setCursor(rows[next])
    rows[next].focus()
  }

  _setCursor(row) {
    this._cursorEl = row
    this._normalizeTabindex()
    this._syncActiveDescendant(row)
  }

  // aria-activedescendant on the owning listbox (spec pin 8): the cursor row
  // is spoken without stealing focus semantics from the roving tabindex.
  _syncActiveDescendant(row) {
    const list = row?.closest('[role="listbox"], [data-container-id]')
    if (!list) return
    if (row) list.setAttribute("aria-activedescendant", row.id || row.getAttribute("data-work-row-id"))
    else list.removeAttribute("aria-activedescendant")
  }

  _setInitialCursor() {
    if (this._cursorEl) return
    const first = this._rows()[0]
    if (first) this._setCursor(first)
  }

  _normalizeTabindex() {
    for (const row of this._rows()) row.tabIndex = row === this._cursorEl ? 0 : -1
  }

  _onFocusIn(event) {
    const row = event.target.closest?.("[data-work-row-id]")
    if (row && row !== this._cursorEl) this._setCursor(row)
  }

  _isFormField(element) {
    const tag = element.tagName ? element.tagName.toLowerCase() : ""
    if (tag === "input" || tag === "textarea" || tag === "select") return true
    if (element.isContentEditable) return true
    return element.closest("input, textarea, select, [contenteditable='true']") !== null
  }

  // ── Kernel drop translation (Track A items 1-4) ─────────────────────────
  // The kernel applies the optimistic single-item move, then emits
  // ui--work:drop. This engine classifies the container, gates the payload
  // shape, groups the selection, and emits exactly one domain event.
  _translateKernelDrop(event) {
    const detail = event.detail
    if (!detail || typeof detail.gesture_token !== "string" || detail.gesture_token === "") return
    const token = detail.gesture_token
    const source = detail.source === "keyboard" ? "keyboard" : "pointer"
    const fromId = detail.from_container
    const classification = this._classifyContainer(detail.container)
    const movedIds = this._resolveMovedIds(detail)
    this._gestureActive = false

    // Membership marker: revert the optimistic move, emit a null-neighbour
    // membership payload (both neighbours null is the contract's "no rank
    // slot" — the host applies membership and re-renders).
    if (classification.type === "membership") {
      this._kernel()?.revert(token)
      this._clearGestureState()
      if (!movedIds.length) return
      this._adjustSectionCounts(movedIds, fromId, classification.toContainer)
      this._emit(BACKLOG_DROP_EVENT, {
        issue_ids: movedIds,
        from_container: fromId,
        to_container: classification.toContainer,
        kind: "membership",
        before_id: null,
        after_id: null,
        gesture_token: token,
        source,
      })
      return
    }

    // Rail node: same revert-then-emit shape, one level of semantics up.
    if (classification.type === "assign") {
      this._kernel()?.revert(token)
      this._clearGestureState()
      if (!movedIds.length) return
      this._emit(ASSIGN_EVENT, {
        issue_ids: movedIds,
        kind: classification.kind,
        target_id: classification.targetId,
        gesture_token: token,
        source,
      })
      return
    }

    // Rank family — the payload-shape gate runs before anything is emitted.
    if (!this._validRankPayload(detail)) {
      this._kernel()?.revert(token)
      console.error(
        "[ui--work-backlog] rank drop rejected: neighbour deltas (before_id/after_id + string moved_ids) are the only accepted payload; got",
        detail,
      )
      return
    }

    if (detail.container === fromId) {
      // No-op detection: the container's order is unchanged after the kernel
      // apply — emit nothing, push no undo entry (guard: no-push-on-noop).
      if (this._isSameOrder(fromId)) {
        this._clearGestureState()
        return
      }
      const rankDetail =
        source === "keyboard"
          ? this._rankDetailForKeyboard(detail, movedIds)
          : this._rankDetail(detail, movedIds)
      this._applyGroupDom(detail, movedIds)
      this._commitRank(rankDetail, movedIds)
      return
    }

    // Cross-container rank move.
    const rankDetail = this._rankDetail(detail, movedIds)
    this._applyGroupDom(detail, movedIds)
    this._adjustSectionCounts(movedIds, fromId, detail.container)
    this._emit(BACKLOG_DROP_EVENT, {
      issue_ids: movedIds,
      from_container: fromId,
      to_container: detail.container,
      kind: "rank",
      before_id: rankDetail.before_id,
      after_id: rankDetail.after_id,
      gesture_token: token,
      source,
    })
    this._pushUndoEntry(movedIds, detail.container)
    this._announceMove(movedIds)
    this._clearGestureState()
  }

  // marker:sprint:<id> → membership into sprint:<id>; epic:/version: → rail
  // assign; anything else is a rank container.
  _classifyContainer(containerId) {
    if (typeof containerId !== "string" || containerId === "") {
      return { type: "rank" }
    }
    if (containerId.startsWith(MARKER_PREFIX)) {
      return { type: "membership", toContainer: containerId.slice(MARKER_PREFIX.length) }
    }
    for (const [prefix, kind] of Object.entries(RAIL_PREFIXES)) {
      if (containerId.startsWith(prefix)) {
        return { type: "assign", kind, targetId: containerId.slice(prefix.length) }
      }
    }
    return { type: "rank" }
  }

  // The contract gate: a rank payload is neighbour deltas plus string ids --
  // index/position fields and full-order arrays never enter the kit. The
  // engine refuses (revert + console.error, no emit) AND the test pins assert
  // the emitted shape, so a regression fails on both sides.
  _validRankPayload(detail) {
    if (!Array.isArray(detail.moved_ids) || detail.moved_ids.length === 0) return false
    if (!detail.moved_ids.every((id) => typeof id === "string" && id !== "")) return false
    if ("index" in detail || "position" in detail) return false
    if (!(detail.before_id === null || typeof detail.before_id === "string")) return false
    if (!(detail.after_id === null || typeof detail.after_id === "string")) return false
    return true
  }

  // Build the rank-reorder payload from the kernel detail. Neighbours pass
  // through verbatim (translate-verbatim rule); moved ids are the resolved
  // selection group in origin order.
  _rankDetail(detail, movedIds) {
    return {
      container: detail.container,
      moved_ids: movedIds,
      before_id: detail.before_id ?? null,
      after_id: detail.after_id ?? null,
      gesture_token: detail.gesture_token,
      source: detail.source === "keyboard" ? "keyboard" : "pointer",
    }
  }

  // Keyboard-sourced rank drops share the pointer payload builder -- the
  // neighbour fields are computed identically and only the cursor-follow
  // differs afterward. Kept as a named seam so the keyboard path can evolve
  // independently of the pointer math.
  _rankDetailForKeyboard(detail, movedIds) {
    return this._rankDetail(detail, movedIds)
  }

  // Selection-integrated moves: when the dragged row is part of the active
  // selection, the whole selection group in the origin container moves as
  // one gesture and the payload carries them in ORIGIN order.
  _resolveMovedIds(detail) {
    const dragged = Array.isArray(detail.moved_ids) ? detail.moved_ids[0] : null
    const group = this._dragSelection
    if (dragged && group && group.ids.includes(dragged)) {
      return group.ids.filter((id) => this._rowById(id))
    }
    return Array.isArray(detail.moved_ids) ? [...detail.moved_ids] : []
  }

  // DOM half of a group move: the kernel already placed the dragged row at
  // the slot; the remaining selected rows arrange around it preserving their
  // origin internal order (rows before the dragged one above, rows after
  // below).
  _applyGroupDom(detail, movedIds) {
    if (!movedIds || movedIds.length < 2) return
    const draggedId = detail.moved_ids[0]
    const anchor = this._rowById(draggedId)
    if (!anchor) return
    const split = movedIds.indexOf(draggedId)
    let beforeAnchor = anchor
    for (let i = split - 1; i >= 0; i -= 1) {
      const el = this._rowById(movedIds[i])
      if (el && el !== beforeAnchor) {
        beforeAnchor.before(el)
        beforeAnchor = el
      }
    }
    let afterAnchor = anchor
    for (let i = split + 1; i < movedIds.length; i += 1) {
      const el = this._rowById(movedIds[i])
      if (el && el !== afterAnchor) {
        afterAnchor.after(el)
        afterAnchor = el
      }
    }
    this._setCursor(anchor)
  }

  _isSameOrder(containerId) {
    const origin = this._gestureOrigin
    if (!origin || origin.container !== containerId) return false
    return origin.order.join(" ") === this._orderOf(containerId).join(" ")
  }

  _orderOf(containerId) {
    const el = this._containerEl(containerId)
    return el ? this._orderWithin(el) : []
  }

  _orderWithin(containerEl) {
    return [...containerEl.querySelectorAll("[data-work-row-id]")].map((el) =>
      el.getAttribute("data-work-row-id"),
    )
  }

  _containerEl(containerId) {
    if (typeof containerId !== "string" || containerId === "") return null
    return this.element.querySelector(`[data-container-id="${CSS.escape(containerId)}"]`)
  }

  _commitRank(rankDetail, movedIds) {
    this._emit(RANK_REORDER_EVENT, rankDetail)
    this._pushUndoEntry(movedIds, rankDetail.container)
    this._announceMove(movedIds)
    this._cursorFollow(rankDetail.moved_ids)
    this._clearGestureState()
  }

  // -- Undo/redo (Track A item 6) ------------------------------------------
  // createUndoStack owns the stack discipline (cap 100, applying guard); the
  // engine owns what a snapshot means: the full row order of every kernel
  // container on the surface. Redo entries live alongside (the stack helper
  // is the undo side only, flow parity).

  _captureAllOrder() {
    const snapshot = []
    for (const el of this.element.querySelectorAll("[data-container-id]")) {
      snapshot.push({ id: el.getAttribute("data-container-id"), ids: this._orderWithin(el) })
    }
    return snapshot
  }

  _pushUndoEntry(movedIds, containerId) {
    if (!this._gestureOrigin || !this._gestureOrigin.all) return
    const entry = { before: this._gestureOrigin.all, after: this._captureAllOrder() }
    // The gesture's own moved ids + target container name the restore delta
    // unambiguously (a before/after diff of a swap is a coin-flip between
    // the moved row and the displaced neighbour — the commit must re-persist
    // the row the USER moved).
    if (Array.isArray(movedIds) && movedIds.length > 0 && typeof containerId === "string" && containerId !== "") {
      entry.moved = movedIds
      entry.container = containerId
    }
    this._undo.push(entry)
    this._redoEntries = []
  }

  undo() {
    const entry = this._undo.undo()
    if (!entry) return false
    this._redoEntries.push(entry)
    this._restoreSnapshot(entry.before)
    this._emitRestore(entry.before, "undo", entry)
    this._announceKey("undone")
    return true
  }

  redo() {
    const entry = this._redoEntries.pop()
    if (!entry) return false
    this._undo.push(entry) // re-register so undo can take it back
    this._restoreSnapshot(entry.after)
    this._emitRestore(entry.after, "redo", entry)
    this._announceKey("redone")
    return true
  }

  // The converged restore path: every row returns to its snapshot container
  // in snapshot order. Restores push nothing (the applying guard from
  // lib/work/undo is set around the mutation); the commit-shaped emit for
  // undo/redo lives in _emitRestore, called by the only two callers of this
  // path (undo/redo — there is no programmatic/apply-driven restore).
  _restoreSnapshot(snapshot) {
    if (!Array.isArray(snapshot)) return
    this._undo.applying = true
    try {
      for (const section of snapshot) {
        const containerEl = this._containerEl(section.id)
        if (!containerEl) continue
        for (const id of section.ids) {
          const row = this._rowById(id)
          if (row) containerEl.appendChild(row)
        }
      }
      this._normalizeTabindex()
    } finally {
      this._undo.applying = false
    }
  }

  // Public seam for hosts that route their own revert UX through the engine:
  // delegates to the kernel's snapshot revert and drops this engine's
  // gesture bookkeeping with it.
  revertGesture(token) {
    this._kernel()?.revert(token)
    this._clearGestureState()
  }

  // -- Restore commits (the bar-commit ruling, backlog edition) -------------
  // A restore that emitted nothing strands the server on the superseded
  // order — the documented glue POSTs every commit-shaped event (the W8F
  // ruling timeline_controller.js implements for bar geometry). undo/redo
  // therefore re-emit ONE commit-shaped rank-reorder built from the
  // RESTORED neighbour delta, plus `action: "undo"|"redo"`. Tokenless by
  // the sansToken contract (a tokenless emit carries no rollback handle;
  // the gesture's own token stayed spent). revertGesture stays eventless:
  // the HOST called it, so the host owns the compensating write.

  _emitRestore(restored, action, entry) {
    // Precise path: rank gestures stamp the entry with the gesture's moved
    // ids, so the restore commit names exactly the rows the user moved —
    // the same payload discipline as the gesture path. The commit lands on
    // the container where the moved rows SIT in the restored order (the one
    // that gained them; rows pulled out of the other are not nameable
    // there). A selection-group move is contiguous in the DESTINATION but
    // may be non-contiguous in the restored (origin) order — one neighbour
    // delta would then claim the group sits together between its outer
    // neighbours. So the present moved rows are grouped into maximal
    // contiguous runs in the restored order and each run gets its own
    // truthful commit. Total absence (the section itself is gone) names
    // nothing: no run, no emit. A PARTIALLY deleted group still commits
    // over the snapshot order — naming a since-deleted id is the host's
    // no-op, and the DOM (already restored) is the truth of record.
    if (entry && Array.isArray(entry.moved) && entry.moved.length > 0) {
      const movedSet = new Set(entry.moved)
      const section = restored.find((s) => s.ids.some((id) => movedSet.has(id)))
      if (section) {
        let run = []
        for (const id of section.ids) {
          if (movedSet.has(id)) {
            run.push(id)
            continue
          }
          if (run.length) this._emitRun(section.id, run, section.ids, action)
          run = []
        }
        if (run.length) this._emitRun(section.id, run, section.ids, action)
      }
      return
    }
    // Unstamped entry: rank gestures always stamp (the kernel's optimistic
    // apply guarantees the resolved group is non-empty) and no other path
    // pushes an undo entry — if one ever arrives, the silent restore would
    // strand the server per the invariant above. Say so loudly instead.
    console.error("[ui--work-backlog] undo entry without moved ids — restore emits no commit")
  }

  // One contiguous run's commit: neighbour deltas against the restored
  // order with the moved ids removed — the same payload discipline as the
  // gesture path.
  _emitRun(container, moved, order, action) {
    this._emit(RANK_REORDER_EVENT, {
      container,
      moved_ids: moved,
      before_id: this._neighbourOf(moved, order, -1),
      after_id: this._neighbourOf(moved, order, 1),
      action,
    })
  }

  // The moved set's neighbour in `order` (which contains the moved rows):
  // direction -1 walks left from the first moved row, +1 right from the
  // last, skipping other moved rows — null when the edge is the container.
  _neighbourOf(moved, order, direction) {
    const movedSet = new Set(moved)
    let first = -1
    let last = -1
    order.forEach((id, index) => {
      if (!movedSet.has(id)) return
      if (first === -1) first = index
      last = index
    })
    if (first === -1) return null
    if (direction === -1) {
      for (let i = first - 1; i >= 0; i -= 1) {
        if (!movedSet.has(order[i])) return order[i]
      }
      return null
    }
    for (let i = last + 1; i < order.length; i += 1) {
      if (!movedSet.has(order[i])) return order[i]
    }
    return null
  }

  // -- Gesture bookkeeping --------------------------------------------------

  _captureGestureStart(event) {
    const detail = event.detail
    if (!detail) return
    const draggedId = detail.item_id ?? (Array.isArray(detail.moved_ids) ? detail.moved_ids[0] : null)
    if (typeof draggedId !== "string" || draggedId === "") return
    const row = this._rowById(draggedId)
    const containerId = row?.closest("[data-container-id]")?.getAttribute("data-container-id") ?? null
    const order = containerId ? this._orderOf(containerId) : []
    // Group = the selected rows inside the dragged row's origin container,
    // in origin DOM order (cross-container selections move only their
    // same-container members; the rest stay selected in place).
    const selected = order.filter((id) => this._rowById(id)?.getAttribute("data-selected") === "true")
    this._dragSelection = selected.includes(draggedId) && selected.length > 1
      ? { container: containerId, ids: selected }
      : null
    this._gestureOrigin = { container: containerId, order, all: this._captureAllOrder() }
    this._gestureActive = true
  }

  _clearGestureState() {
    this._dragSelection = null
    this._gestureOrigin = null
    this._gestureActive = false
  }

  // After a keyboard rank the cursor rides the dragged row -- and keeps
  // focus (the kernel's DOM move drops focus on body in some browsers).
  _cursorFollow(movedIds) {
    const id = Array.isArray(movedIds) ? movedIds[movedIds.length - 1] : null
    const row = this._rowById(id)
    if (row) {
      this._setCursor(row)
      row.focus()
    }
  }

  _kernel() {
    if (this._kernelRef === undefined) {
      try {
        this._kernelRef = this.application.getControllerForElementAndIdentifier(this.element, "ui--work-drag") || null
      } catch {
        this._kernelRef = null
      }
    }
    return this._kernelRef
  }

  _emit(name, detail) {
    this.element.dispatchEvent(new CustomEvent(name, { bubbles: true, cancelable: true, detail }))
  }

  _announceKey(key, tokens = {}) {
    announce(this._announcer, this._announcements[key] ?? "", tokens)
  }

  _announceMove(movedIds) {
    const count = movedIds.length
    if (count === 1) {
      this._announceKey("moved", { issue_key: movedIds[0] })
    } else {
      this._announceKey("moved_many", { count: String(count) })
    }
  }

  // -- Section counters (optimistic sugar) ----------------------------------
  // The host re-renders authoritative counts; between event and re-render
  // the visible count/points of both sections step by the moved rows.
  _adjustSectionCounts(movedIds, fromContainer, toContainer) {
    if (!movedIds.length) return
    // Fractional story points are real ("0.5"): parseFloat + the finite
    // guard (rollups.js's non-number refusal), never parseInt — "0.5" must
    // count as 0.5, not 0.
    const points = movedIds.reduce((sum, id) => {
      const raw = this._rowById(id)?.getAttribute("data-work-points")
      const value = Number.parseFloat(raw)
      return Number.isFinite(value) ? sum + value : sum
    }, 0)
    this._bumpSection(fromContainer, -movedIds.length, -points)
    this._bumpSection(toContainer, movedIds.length, points)
  }

  _bumpSection(containerId, countDelta, pointsDelta) {
    const section = this._sectionEl(containerId)
    if (!section) return
    const countEl = section.querySelector("[data-work-section-count]")
    if (countEl) {
      const current = Number.parseInt(countEl.textContent ?? "0", 10)
      if (Number.isFinite(current)) countEl.textContent = String(current + countDelta)
    }
    if (pointsDelta !== 0) {
      const pointsEl = section.querySelector("[data-work-section-points]")
      if (pointsEl) {
        // Same fractional-points rule as _adjustSectionCounts: an
        // already-fractional displayed total must not truncate to an int
        // base. Counts stay integer-parsed above.
        const current = Number.parseFloat(pointsEl.textContent ?? "0")
        if (Number.isFinite(current)) pointsEl.textContent = String(current + pointsDelta)
      }
    }
  }

  _sectionEl(containerId) {
    if (containerId === "backlog") {
      return this.element.querySelector('[data-work-section="backlog"]')
    }
    if (typeof containerId === "string" && containerId.startsWith("sprint:")) {
      const sprintId = containerId.slice("sprint:".length)
      return this.element.querySelector(`[data-work-sprint-id="${CSS.escape(sprintId)}"]`)
    }
    return null
  }

  // -- Root clicks: sprint toggles, sprint actions, completion modal, rail ---
  // Delegated on the root so host re-renders never rewire handlers. The
  // collapsible trigger's own data-action runs first (target phase), so by
  // the time this reads aria-expanded the accordion has already flipped.
  _onRootClick(event) {
    const toggle = event.target.closest("[data-work-sprint-toggle]")
    if (toggle) {
      this._syncCollapse(toggle)
      return
    }
    const action = event.target.closest("[data-work-sprint-action-id]")
    if (action) {
      this._onSprintAction(action)
      return
    }
    const destination = event.target.closest("[data-work-sprint-destination]")
    if (destination) {
      this._onSprintDestination(destination)
      return
    }
    const cancel = event.target.closest("[data-work-sprint-cancel]")
    if (cancel) {
      cancel.closest("[data-work-sprint-modal]")?.setAttribute("hidden", "")
      return
    }
    // The load-more button is engine-operable: a real focusable control
    // whose click routes to loadMore (the in-flight ref guard protects
    // double-fires; the IntersectionObserver arms the automatic path).
    const sentinel = event.target.closest("[data-work-load-more]")
    if (sentinel) {
      this.loadMore(sentinel.getAttribute("data-work-load-more"))
    }
  }

  _syncCollapse(toggle) {
    const section = toggle.closest("[data-work-sprint-id]")
    const sprintId = section?.getAttribute("data-work-sprint-id")
    if (!sprintId) return
    const stateEl = this._expandedStateEl(toggle)
    if (!stateEl) return
    const collapsed = stateEl.getAttribute("aria-expanded") === "false"
    this._collapsed.set(sprintId, collapsed)
  }

  // The toggle target may carry its own aria-expanded (bare fixtures) or sit
  // inside the collapsible trigger div that owns it (SprintMarkerComponent).
  _expandedStateEl(toggle) {
    if (toggle.getAttribute("aria-expanded") !== null) return toggle
    return toggle.closest("[aria-expanded]")
  }

  _onSprintAction(actionEl) {
    if (actionEl.disabled || actionEl.getAttribute("aria-disabled") === "true") return
    const actionId = actionEl.getAttribute("data-work-sprint-action-id")
    // Dropdown-menu items render in a body portal (outside the section), so
    // the sprint id rides the item itself first; closest() covers inline
    // actions.
    const sprintId =
      actionEl.getAttribute("data-work-sprint-id") ||
      actionEl.closest("[data-work-sprint-id]")?.getAttribute("data-work-sprint-id")
    if (!actionId || !sprintId) return
    if (actionEl.hasAttribute("data-work-sprint-prompt")) {
      const modal = this.element.querySelector(`[data-work-sprint-modal="${CSS.escape(sprintId)}"]`)
      if (!modal) return
      modal.removeAttribute("hidden")
      modal.querySelector("[data-work-sprint-destination]")?.focus()
      return
    }
    this._emit(SPRINT_ACTION_EVENT, { sprint_id: sprintId, action_id: actionId })
  }

  _onSprintDestination(destinationEl) {
    const modal = destinationEl.closest("[data-work-sprint-modal]")
    if (!modal || modal.hasAttribute("hidden")) return
    const sprintId = modal.getAttribute("data-work-sprint-modal")
    const destination = destinationEl.getAttribute("data-work-sprint-destination")
    if (!sprintId || !destination) return
    this._emit(SPRINT_COMPLETE_EVENT, { sprint_id: sprintId, destination })
    modal.setAttribute("hidden", "")
  }

  // Rail select: the tree dispatches tree:select on its root <ul> with
  // { value } -- the rail kind comes from the node's [data-rail-kind]
  // wrapper (the tree payload has no kind slot).
  _onRailSelect(event) {
    const node = event.target.closest("[data-rail-kind]")
    if (!node) return
    this._emit(ASSIGN_EVENT, {
      issue_ids: this._dragSelection ? [...this._dragSelection.ids] : [],
      kind: node.getAttribute("data-rail-kind"),
      target_id: event.detail?.value,
      gesture_token: null,
      source: "rail",
    })
  }

  // -- Post-render re-apply (Turbo morphs / host re-renders) ----------------
  _scheduleReapply() {
    if (this._reapplyQueued) return
    this._reapplyQueued = true
    window.setTimeout(() => {
      this._reapplyQueued = false
      if (!this._bound) return
      this._reapply()
    }, 0)
  }

  _reapply() {
    this._mutation?.takeRecords()
    this._reapplyCollapsed()
    this._setInitialCursor()
    this._normalizeTabindex()
    this._armSentinels()
  }

  // Collapsed-state survival: if the map says collapsed but the fresh SSR
  // trigger reads expanded, re-flip through the trigger itself so the
  // accordion controller does the collapsing (no accordion internals here).
  _reapplyCollapsed() {
    for (const section of this.element.querySelectorAll("[data-work-sprint-id]")) {
      const sprintId = section.getAttribute("data-work-sprint-id")
      const collapsed = this._collapsed.get(sprintId)
      if (collapsed === undefined) continue
      const toggle = section.querySelector("[data-work-sprint-toggle]")
      if (!toggle) continue
      const stateEl = this._expandedStateEl(toggle)
      if (!stateEl) continue
      if ((stateEl.getAttribute("aria-expanded") === "false") !== collapsed) toggle.click()
    }
  }

  // -- Lazy append (Track D items 1-2) --------------------------------------
  // Public seam: the IntersectionObserver calls it, hosts may call it, tests
  // call it. Turbo-optional transport: when the sentinel carries a Turbo
  // Frame the engine sets src (D1 host transport); otherwise it fetches the
  // page through @rails/request.js and appends the parsed rows.
  loadMore(sectionId) {
    if (!sectionId || this._inFlight.has(sectionId)) return
    const sentinel = this.element.querySelector(`[data-work-load-more="${CSS.escape(sectionId)}"]`)
    if (!sentinel) return
    const url = sentinel.getAttribute("data-work-page-url")
    if (!url) return
    this._inFlight.add(sectionId)
    const container = this._containerEl(sectionId)
    container?.setAttribute("aria-busy", "true")
    this._insertSkeletons(container)
    const frame = sentinel.querySelector("turbo-frame")
    if (frame && "src" in frame) {
      frame.src = url
      this._armFrameTimeout(sectionId)
      return
    }
    this._fetchRows(sectionId, url)
  }

  async _fetchRows(sectionId, url) {
    try {
      const { FetchRequest } = await import("@rails/request.js")
      const response = await new FetchRequest("get", url).perform()
      if (!response.ok) throw new Error(`backlog page load failed: ${response.statusCode}`)
      const html = await response.text
      const doc = new DOMParser().parseFromString(html, "text/html")
      this._finishLoad(sectionId, this._extractPage(doc))
    } catch (error) {
      console.error("[ui--work-backlog] lazy append failed:", error)
      this._abortLoad(sectionId)
    }
  }

  // turbo:frame-load: the frame swapped in the next page's fragment --
  // harvest its rows, then empty the frame so a later page cannot duplicate.
  _onFrameLoad(event) {
    const frame = event.target
    if (!(frame instanceof Element) || !frame.hasAttribute("data-work-page-frame")) return
    const sectionId = frame.getAttribute("data-work-page-frame")
    if (!this._inFlight.has(sectionId)) return
    const page = this._extractPage(frame)
    frame.textContent = ""
    this._finishLoad(sectionId, page)
  }

  // turbo:frame-missing: Turbo's real failure signal for a frame load — a
  // response arrived but held no matching frame. A hard network failure
  // dispatches NOTHING, so the timeout armed at frame.src covers that half;
  // whichever fires first unwedges the section exactly like the
  // FetchRequest branch's catch (in-flight ref released, skeletons removed,
  // aria-busy cleared, sentinel re-arms for a retry).
  _onFrameMissing(event) {
    const frame = event.target
    if (!(frame instanceof Element) || !frame.hasAttribute("data-work-page-frame")) return
    const sectionId = frame.getAttribute("data-work-page-frame")
    if (!this._inFlight.has(sectionId)) return
    // Cancelable in Turbo 8 (the vendored source dispatches with
    // cancelable:!0); left standing, Turbo's default renders its "Content
    // missing" error markup INTO the frame and throws. The engine owns the
    // failure: unwedge, no injected markup, no throw.
    event.preventDefault()
    this._abortLoad(sectionId)
  }

  _armFrameTimeout(sectionId) {
    const timer = setTimeout(() => {
      this._frameTimeouts.delete(sectionId)
      if (this._inFlight.has(sectionId)) this._abortLoad(sectionId)
    }, FRAME_LOAD_TIMEOUT)
    this._frameTimeouts.set(sectionId, timer)
  }

  _clearFrameTimeout(sectionId) {
    const timer = this._frameTimeouts.get(sectionId)
    if (timer !== undefined) {
      clearTimeout(timer)
      this._frameTimeouts.delete(sectionId)
    }
  }

  // A page fragment carries a [data-work-page] wrapper whose attributes say
  // whether more pages follow (data-work-has-more) and their URL
  // (data-work-page-url); its [data-work-row-id] children are the rows.
  _extractPage(root) {
    const page = root.querySelector("[data-work-page]")
    if (!page) return { rows: [], nextUrl: null, hasMore: false }
    return {
      rows: [...page.querySelectorAll("[data-work-row-id]")],
      nextUrl: page.getAttribute("data-work-page-url"),
      hasMore: page.getAttribute("data-work-has-more") !== "false",
    }
  }

  _finishLoad(sectionId, page) {
    this._clearFrameTimeout(sectionId)
    this._inFlight.delete(sectionId)
    const container = this._containerEl(sectionId)
    container?.querySelectorAll("[data-work-skeleton]").forEach((el) => el.remove())
    container?.removeAttribute("aria-busy")
    if (container && page.rows.length) {
      container.append(...page.rows)
    }
    this._setInitialCursor()
    this._normalizeTabindex()
    const sentinel = this.element.querySelector(`[data-work-load-more="${CSS.escape(sectionId)}"]`)
    if (sentinel) {
      if (page.hasMore && page.nextUrl) {
        sentinel.setAttribute("data-work-page-url", page.nextUrl)
      } else {
        this._observedSentinels.get(sentinel)?.disconnect()
        this._observedSentinels.delete(sentinel)
        sentinel.remove()
      }
    }
    this._announceKey("loaded", { count: String(page.rows.length) })
  }

  _abortLoad(sectionId) {
    this._clearFrameTimeout(sectionId)
    this._inFlight.delete(sectionId)
    const container = this._containerEl(sectionId)
    container?.querySelectorAll("[data-work-skeleton]").forEach((el) => el.remove())
    container?.removeAttribute("aria-busy")
    // A Turbo-frame transport keeps the URL it failed to load — and Turbo
    // skips a re-assignment of the identical src (srcChanged no-ops on no
    // change), so the retry would silently do nothing. The frame forgets
    // the URL (and Turbo's own complete stamp from the missing-response
    // path) so a retry is a genuine navigation — the command-palette
    // controller's retry idiom.
    const sentinel = this.element.querySelector(`[data-work-load-more="${CSS.escape(sectionId)}"]`)
    const frame = sentinel?.querySelector("turbo-frame")
    frame?.removeAttribute("src")
    frame?.removeAttribute("complete")
  }

  _insertSkeletons(container) {
    if (!container) return
    for (let i = 0; i < SKELETON_ROWS; i += 1) {
      const li = document.createElement("li")
      li.setAttribute("data-work-skeleton", "")
      li.setAttribute("aria-hidden", "true")
      li.className = "bg-muted animate-pulse rounded-md h-9"
      container.appendChild(li)
    }
  }

  _armSentinels() {
    const IO = window.IntersectionObserver
    if (typeof IO !== "function") return
    for (const sentinel of this.element.querySelectorAll("[data-work-load-more]")) {
      if (this._observedSentinels.has(sentinel)) continue
      const sectionId = sentinel.getAttribute("data-work-load-more")
      const observer = new IO((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.loadMore(sectionId)
        }
      }, { rootMargin: "200px" })
      observer.observe(sentinel)
      this._observedSentinels.set(sentinel, observer)
    }
  }
}
